import { MessageBarButton } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPDefaultButton, IPLink, IPPrimaryButton, IPText } from 'baseComponents';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPMessageBar, IPMessageBarType } from 'baseComponents/MessageBar/IPMessageBar';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import EditTextFieldCallOut from 'components/CallOuts/EditTextFieldCallOut';
import CategoryChips from 'components/Tags/CategoryChips';
import ColorField from 'components/Tags/ColorField';
import { globalAppName } from 'globalConstants';
import { globalStackTokensGapMedium, globalStackTokensGapSmall, globalTextStylesValidationError } from 'globalStyles';
import Tag, { TagInvalidChars, TagSystemTypes } from 'models/tag';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sortOnTag } from 'utils/sorting';

interface ITagEditorProps {
  tag: Tag;
  tags: Tag[];
  onClose: () => void;
  onUpdate: (tag: Tag) => void | Promise<void>;
  // The host panel (TagFormPanel) renders Save/Cancel; the editor registers its save + disabled here.
  onSaveCallback: (save: () => void | Promise<void>) => void;
  onSaveDisabledCallback: (isDisabled: () => boolean) => void;
}

//
// Edit form for an existing tag. Mirrors TagCreator but with free-text Group/Value (you are renaming an
// existing tag, not picking from existing groups). Shares ColorField + CategoryChips so create and edit
// look and behave identically.
//
const TagEditor = (props: ITagEditorProps) => {
  const { t } = useTranslation(['translation', 'tag', 'exceptions']);
  const appContext = useContext(AppContext);
  const [updatedTag, setUpdatedTag] = useState<Tag>(props.tag);
  const [error, setError] = useState<string | undefined>(undefined);
  // Use the fresh admin-list tags, not the global cache: the cache can be stale while this panel is open.
  const [tags, setTags] = useState<Tag[]>(() => [...props.tags]);
  const [editTagInfoCallout, setEditTagInfoCallout] = useState<boolean>(false);
  // Inline "Fix it" guidance shown when the user tries to move the last tag out of its category.
  const [showCategoryHelp, setShowCategoryHelp] = useState<boolean>(false);
  const [showFix, setShowFix] = useState<boolean>(false);
  const [fixSelection, setFixSelection] = useState<number | undefined>(undefined);

  const calloutId = useMemo(() => 'tag-editor-' + Math.random().toString(36).substring(7), []);

  useEffect(() => setTags([...props.tags]), [props.tags]);

  useEffect(() => {
    if (
      updatedTag.tagName.toLowerCase() === props.tag.tagName.toLowerCase() &&
      updatedTag.tagValue.toLowerCase() === props.tag.tagValue.toLowerCase()
    ) {
      return;
    }

    const exists = tags.some(
      (t) =>
        t.tagName.toLowerCase() === updatedTag.tagName.toLowerCase() &&
        t.tagValue.toLowerCase() === updatedTag.tagValue.toLowerCase(),
    );
    if (exists) {
      setError(t('tag:Editor.NotUnique'));

      return;
    }

    if (updatedTag.tagName.trim().length === 0 || updatedTag.tagValue.trim().length === 0) {
      setError(t('tag:Editor.Required'));

      return;
    }

    setError(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTag]);

  const doSave = async () => {
    const tag = updatedTag.clone();
    tag.tagName = tag.tagName.trim();
    tag.tagValue = tag.tagValue.trim();
    try {
      await props.onUpdate(tag);
    } catch {
      return;
    }
    props.onClose();
  };

  // Disable Save until the tag actually differs from the original -- saving an unchanged tag is a no-op.
  const isDirty = (): boolean =>
    updatedTag.tagName !== props.tag.tagName ||
    updatedTag.tagValue !== props.tag.tagValue ||
    updatedTag.tagColor !== props.tag.tagColor ||
    (updatedTag.tagInfo ?? '') !== (props.tag.tagInfo ?? '') ||
    updatedTag.tagSystemType !== props.tag.tagSystemType;

  const isSaveDisabled = (): boolean => error !== undefined || !isDirty();

  // Register save + disabled with the host panel; re-register when the edited tag or error changes.
  useEffect(() => {
    props.onSaveCallback(doSave);
    props.onSaveDisabledCallback(isSaveDisabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTag, error]);

  const onNameChange = (newValue?: string) => {
    TagInvalidChars.forEach((c) => (newValue = newValue ? newValue.replaceAll(c, '-') : newValue));
    const tag = updatedTag.clone();
    tag.tagName = newValue ?? '';
    setUpdatedTag(tag);
  };

  const onValueChange = (newValue?: string) => {
    TagInvalidChars.forEach((c) => (newValue = newValue ? newValue.replaceAll(c, '-') : newValue));
    const tag = updatedTag.clone();
    tag.tagValue = newValue ?? '';
    setUpdatedTag(tag);
  };

  const onColorChange = (color: string) => {
    const tag = updatedTag.clone();
    tag.tagColor = color;
    setUpdatedTag(tag);
  };

  const onInfoChange = (newValue?: string) => {
    const tag = updatedTag.clone();
    tag.tagInfo = newValue;
    setUpdatedTag(tag);
  };

  // None and undefined both mean "no category", and a value outside the enum (a removed category) has
  // no name. Only a real category is protected by the one-tag-per-category rule.
  const isRealCategory = (systemType: number | undefined): boolean =>
    systemType !== undefined && systemType !== TagSystemTypes.None && systemType in TagSystemTypes;

  // The lock follows the persisted category, as the API guard does. The chip selection in this panel is
  // not saved yet, so it can never lock the tag.
  const lockedCategory = props.tag.tagSystemType;

  const isLocked =
    isRealCategory(lockedCategory) &&
    !tags.some((tag) => tag.tagId !== props.tag.tagId && tag.tagSystemType === lockedCategory);

  const onSystemTypeChange = (systemType: number) => {
    if (systemType === updatedTag.tagSystemType) return;

    // Business rule: every category must keep at least one tag. If this is the last tag carrying its
    // persisted category, block the change and surface the inline "Fix it" guidance instead.
    if (isLocked) {
      setShowCategoryHelp(true);

      return;
    }

    const tag = updatedTag.clone();
    tag.tagSystemType = systemType;
    setUpdatedTag(tag);
  };

  const lockedCategoryText = Tag.GetSystemTypeText(lockedCategory, t);

  // A candidate is unsafe if moving it would empty its own category: the API enforces the same
  // "at least one tag per category" rule, so reassigning the sole tag of a category would be rejected.
  const wouldEmptyOwnCategory = (candidate: Tag): boolean =>
    isRealCategory(candidate.tagSystemType) &&
    !tags.some((other) => other.tagId !== candidate.tagId && other.tagSystemType === candidate.tagSystemType);

  // "Fix it" candidates: every other tag that can safely take the locked category without emptying its own.
  const fixCandidates: IPSelectableOption[] = [...tags]
    .filter((tag) => tag.tagId !== updatedTag.tagId && !wouldEmptyOwnCategory(tag))
    .sort(sortOnTag)
    .map((tag) => ({ key: tag.tagId, text: tag.value() }));

  // "Fix it": give another tag the locked category so this tag's category is free to change.
  const onFixConfirm = async () => {
    const target = tags.find((tag) => tag.tagId === fixSelection);
    if (!target) return;

    const category = lockedCategory;
    const reassigned = target.clone();
    reassigned.tagSystemType = category;
    try {
      await props.onUpdate(reassigned);
    } catch {
      return;
    }

    // Optimistically reflect the reassignment locally so the category unlocks right away.
    setTags((prev) => prev.map((tag) => (tag.tagId === reassigned.tagId ? reassigned : tag)));

    appContext.showNotification(
      t('tag:Editor.FixCategory.Done', {
        tag: target.value(),
        category: Tag.GetSystemTypeText(category, t),
      }),
      false,
      false,
      true,
    );

    setShowFix(false);
    setShowCategoryHelp(false);
    setFixSelection(undefined);
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack horizontal wrap tokens={globalStackTokensGapSmall}>
        <IPStack.Item grow>
          <IPTextField
            autoFocus={!appContext.isMobileView}
            label={t('tag:Creator.Group')}
            required
            maxLength={32}
            value={updatedTag.tagName}
            onChange={(event, newValue) => onNameChange(newValue)}
          />
        </IPStack.Item>
        <IPStack.Item grow>
          <IPTextField
            label={t('tag:Creator.Value')}
            required
            maxLength={64}
            value={updatedTag.tagValue}
            onChange={(event, newValue) => onValueChange(newValue)}
          />
        </IPStack.Item>
      </IPStack>
      {error && (
        <IPStack.Item>
          <IPText styles={globalTextStylesValidationError} variant="small">
            {error}
          </IPText>
        </IPStack.Item>
      )}
      <IPStack.Item>
        <ColorField color={updatedTag.tagColor} onChange={onColorChange} />
      </IPStack.Item>
      <IPStack.Item>
        <IPLink id={calloutId} onClick={() => setEditTagInfoCallout(true)}>
          {t('tag:Editor.Info.Label')}
        </IPLink>
        <EditTextFieldCallOut
          value={updatedTag.tagInfo}
          targetId={calloutId}
          onClose={() => setEditTagInfoCallout(false)}
          onUpdate={onInfoChange}
          maxLenght={512}
          isVisible={editTagInfoCallout}
        />
      </IPStack.Item>
      <IPStack.Item grow>
        <CategoryChips selected={updatedTag.tagSystemType} onChange={onSystemTypeChange} />
      </IPStack.Item>
      {showCategoryHelp && (
        <IPStack.Item>
          <IPMessageBar
            messageBarType={IPMessageBarType.warning}
            isMultiline={true}
            truncated={false}
            onDismiss={() => {
              setShowCategoryHelp(false);
              setShowFix(false);
              setFixSelection(undefined);
            }}
            actions={
              showFix ? undefined : (
                <MessageBarButton onClick={() => setShowFix(true)}>
                  {t('tag:Editor.FixCategory.Button')}
                </MessageBarButton>
              )
            }
          >
            {t('tag:Editor.FixCategory.Message', { category: lockedCategoryText, appName: globalAppName })}
          </IPMessageBar>
          {showFix && (
            <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { marginTop: 8 } }}>
              <IPDropdown
                label={t('tag:Editor.FixCategory.PickLabel', { category: lockedCategoryText })}
                placeholder={t('tag:Editor.FixCategory.PickPlaceholder')}
                selectedKey={fixSelection}
                options={fixCandidates}
                onChange={(event, option?: IPSelectableOption) => setFixSelection(option?.key as number)}
              />
              <IPStack horizontal tokens={globalStackTokensGapSmall}>
                <IPStack.Item>
                  <IPPrimaryButton disabled={fixSelection === undefined} onClick={onFixConfirm}>
                    {t('tag:Editor.FixCategory.Assign')}
                  </IPPrimaryButton>
                </IPStack.Item>
                <IPStack.Item>
                  <IPDefaultButton
                    onClick={() => {
                      setShowFix(false);
                      setFixSelection(undefined);
                    }}
                  >
                    {t('translation:General.Button.Cancel')}
                  </IPDefaultButton>
                </IPStack.Item>
              </IPStack>
            </IPStack>
          )}
        </IPStack.Item>
      )}
    </IPStack>
  );
};

export default TagEditor;
